import React, { useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { Button } from '@/components/ui/button';
import { Copy, Download, Upload, FileCode } from 'lucide-react';
import { toast } from 'sonner';
import { IDS } from '@/constants/testIds/wizard';

const defineMach3Theme = (monaco) => {
  monaco.editor.defineTheme('mach3-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'comment', foreground: '7F8B96' },
      { token: 'number', foreground: '19D96B' },
      { token: 'keyword', foreground: 'E6EDF3' },
    ],
    colors: {
      'editor.background': '#0B0D0E',
      'editorLineNumber.foreground': '#4B5563',
      'editorCursor.foreground': '#00FF41',
      'editor.selectionBackground': '#0B7A3A55',
      'editor.lineHighlightBackground': '#12171A',
      'editorIndentGuide.background': '#263238',
    },
  });
  if (!monaco.languages.getLanguages().find(l => l.id === 'gcode')) {
    monaco.languages.register({ id: 'gcode' });
    monaco.languages.setMonarchTokensProvider('gcode', {
      tokenizer: {
        root: [
          [/\([^)]*\)/, 'comment'],
          [/;.*$/, 'comment'],
          [/[NnGgMm]\d+(?:\.\d+)?/, 'keyword'],
          [/[TtSsFfHhDd]\d+(?:\.\d+)?/, 'type'],
          [/[XxYyZzIiJjKkPpRrAaBbCc]-?\d+(?:\.\d+)?/, 'number'],
        ],
      },
    });
  }
};

export const GCodePanel = ({ gcode, setGcode, onImportRequest, onImportedText, projectName }) => {
  const editorRef = useRef(null);

  const onMount = (editor, monaco) => {
    editorRef.current = editor;
    defineMach3Theme(monaco);
    monaco.editor.setTheme('mach3-dark');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(gcode || '');
      toast.success('G-kod vagolapra masolva');
    } catch { toast.error('Masolas nem sikerult'); }
  };
  const handleDownload = () => {
    const name = (projectName || 'program').replace(/[^A-Za-z0-9_.-]/g, '_');
    const blob = new Blob([gcode || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${name}.tap`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Letoltve: ${name}.tap`);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="panel-header">
        <div className="flex items-center gap-2">
          <FileCode className="h-4 w-4 text-primary" />
          <span className="panel-title">G-kod</span>
          <span className="text-[10px] text-muted-foreground">{(gcode || '').split('\n').length} sor</span>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onImportRequest} data-testid={IDS.right.import}><Upload className="h-3.5 w-3.5 mr-1" />Import</Button>
          <Button size="sm" variant="ghost" onClick={handleCopy} data-testid={IDS.right.copy}><Copy className="h-3.5 w-3.5 mr-1" />Masolas</Button>
          <Button size="sm" variant="secondary" onClick={handleDownload} disabled={!gcode} data-testid={IDS.right.download}><Download className="h-3.5 w-3.5 mr-1" />.tap</Button>
        </div>
      </div>
      <div className="flex-1 min-h-0" data-testid={IDS.right.gcodeEditor}>
        <Editor
          height="100%"
          defaultLanguage="gcode"
          language="gcode"
          theme="mach3-dark"
          value={gcode || ''}
          onChange={(v) => setGcode(v || '')}
          onMount={onMount}
          options={{
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 18,
            minimap: { enabled: false },
            stickyScroll: { enabled: false },
            scrollBeyondLastLine: false,
            renderWhitespace: 'none',
            tabSize: 2,
            wordWrap: 'off',
          }}
        />
      </div>
    </div>
  );
};

export default GCodePanel;
